import { FC, useContext, useEffect, useMemo } from "react";
import { ZipProjectContext } from "@app/projects/zip-projects/ZipProjectContext";
import { useLocation } from "react-router-dom";
import { ZipCodeDisplayModel } from "@app/projects/zip-projects/interfaces/zipCodeDisplayModel";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { NetworkProvidersComparisonPanel } from "@app/projects/servicing-provider/NetworkProvidersComparisonPanel";
import { ZipNextClosestProvidersPanel } from "@app/projects/zip-projects/ZipNextClosestProvidersPanel";
import ProjectRunConfiguration from "@app/projects/projectRunConfiguration";
import { getTopAndBottomRunConfigurations } from "@app/projects/zip-projects/utility";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { ProjectRunConfigurationStatus } from "@app/projects/enumerators";
import { ViewableFieldsContext } from "@app/viewable-fields/ViewableFieldsContext";
import { TableType } from "@app/viewable-fields/viewableFields";
import { ImpactProvidersPanel } from "@app/projects/servicing-provider/ImpactProvidersPanel";
import { NetworkProvidersPanel } from "@app/projects/servicing-provider/NetworkProvidersPanel";

export const ZipProjectProviders: FC = () => {
  const { getLatestRunConfiguration, state } = useContext(ZipProjectContext);
  const viewableFields = useContext(ViewableFieldsContext);
  const projectContext = useContext(ZipProjectContext);
  const location = useLocation();
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;

  const regionSetId = (state.zipDetails[0] as ZipCodeDisplayModel)?.regionSetId;
  const specialtyGroupCode = (state.zipDetails[0] as ZipCodeDisplayModel)?.specialtyGroupCode;
  const zipDetails = state.zipDetails[0] as ZipCodeDisplayModel;
  const showNcp = featureFlags.has(Features.ZIP_NEXT_CLOSEST_PROVIDERS)
    && state.runConfiguration.showNextClosestNetworkProviders
    && !zipDetails.metPassPercent
    && !state.runConfiguration.isProjected
    && !state.runConfiguration.isProjectedTermed
    && (state.ncpProjectRunStatus === ProjectRunConfigurationStatus.Succeeded
      || state.runConfiguration.projectRunConfigurationId !== getLatestRunConfiguration(state.project).projectRunConfigurationId);

  useEffect(() => {
    const providerTypes = [TableType.NetworkProviders];
    if (featureFlags.has(Features.ZIP_PROSPECT_AND_MARKET)) {
      if (!!projectContext.state.runConfiguration.prospectProviderSetId) {
        providerTypes.push(TableType.ProspectProviders);
      }
      if (!!projectContext.state.runConfiguration.marketProviderSetId) {
        providerTypes.push(TableType.MarketProviders)
      }
    }
    if (featureFlags.has(Features.ZIP_CODE_ANALYSIS_TERMINATION) && (state.runConfiguration.isProjectedTermed || state.runConfiguration.isTermed))
    {
      providerTypes.push(TableType.ProviderTerminations)
    }
    viewableFields.getAllViewableFields(providerTypes);
    // network providers panel also requests its own viewable fields 
    // every time the timeslice is changed, which might be necessary in county projects
  }, [projectContext.state.runConfiguration]);

  if (!regionSetId && !specialtyGroupCode) {
    return null;
  }

  const { bottomConfig, topConfig } = useMemo(() => {
    if (!state.isCompareMode) {
      return { bottomConfig: undefined as ProjectRunConfiguration, topConfig: state.runConfiguration };
    }

    return getTopAndBottomRunConfigurations(state.runConfiguration, state.compareToRunConfiguration, state.isCompareMode, state.isDualMembershipMode);
  }, [state.runConfiguration, state.compareToRunConfiguration, state.isCompareMode])

  return (
    <>
      {!state.isCompareMode ? (
        <>
          <NetworkProvidersPanel
            accessSummary={null}
            exportEndpoint={`/projects/${state.project?.projectId}/adequacy/export/${state.runConfiguration?.projectRunConfigurationId}/${state.runConfiguration?.isLatestPublished}`}
            nextClosestProviders={false}
            projectURL={location.pathname + location.search}
            regionSetId={regionSetId}
            specialtyGroupCode={specialtyGroupCode}
          />
          {showNcp &&
            <ZipNextClosestProvidersPanel
              exportEndpoint={`/projects/${state.project?.projectId}/adequacy/export/${state.runConfiguration?.projectRunConfigurationId}/${state.runConfiguration?.isLatestPublished}`}
              projectUrl={location.pathname + location.search}
              regionSetId={regionSetId}
              specialtyGroupCode={specialtyGroupCode}
            />
          }
          {featureFlags.has(Features.ZIP_PROSPECT_AND_MARKET) && !!state.runConfiguration.prospectProviderSetId &&
            <ImpactProvidersPanel
              accessSummary={null}
              exportEndpoint={`/projects/${state.project?.projectId}/adequacy/export/${state.runConfiguration?.projectRunConfigurationId}/${state.runConfiguration?.isLatestPublished}`}
              providerNetworkType={ProviderNetworkTypeEnum.Prospect}
              projectURL={location.pathname + location.search}
              regionSetId={regionSetId}
              specialtyGroupCode={specialtyGroupCode}
            />}
          {featureFlags.has(Features.ZIP_PROSPECT_AND_MARKET) && !!state.runConfiguration.marketProviderSetId &&
            <ImpactProvidersPanel
              accessSummary={null}
              exportEndpoint={`/projects/${state.project?.projectId}/adequacy/export/${state.runConfiguration?.projectRunConfigurationId}/${state.runConfiguration?.isLatestPublished}`}
              providerNetworkType={ProviderNetworkTypeEnum.Market}
              projectURL={location.pathname + location.search}
              regionSetId={regionSetId}
              specialtyGroupCode={specialtyGroupCode}
            />
          }
        </>
      ) : (
        // County projects always use the primary config's isLatestPublished in exportEndpoint for whatever reason, so we'll do the same here
        <NetworkProvidersComparisonPanel
          exportEndpoint={`/projects/${state.project?.projectId}/adequacy/export/${topConfig?.projectRunConfigurationId}/${state.runConfiguration?.isLatestPublished}`}
          projectURL={location.pathname + location.search}
          regionSetId={regionSetId}
          specialtyGroupCode={specialtyGroupCode}
          exportComparedRunConfigurationId={bottomConfig?.projectRunConfigurationId}
        />
      )}
    </>
  )
}